import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import {
    // Stack,
    Flex,
    Button,
    useToast,
    FormControl,
    FormLabel,
    Input,
    Checkbox,
    VStack,
    HStack,
    Box,
    Link,
    Select,
} from '@chakra-ui/react';
import { useUserData } from 'context/UserDataProvider';
import ZaraButton from 'components/custom-components/buttons/Button';
import { PATHS } from 'router/routes';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CreateSubscription } from 'graphql/mutations/CreateSubscription';
import { PaymentMethod, StripeError, StripeCardNumberElement } from '@stripe/stripe-js';
import { useError } from 'error-provider/ErrorProvider';


import { GetMe } from 'graphql/queries/GetMe';
import useServerApi from '../../../hooks/useServerApi';

interface IPaymentMethodResult {
    error?: StripeError | undefined;
    paymentMethod?: PaymentMethod | undefined;
}

// Extend Window type to include fbq for Facebook Pixel
declare global {
    interface Window {
        fbq?: (...args: any[]) => void;
    }
}

interface Props {
    priceId: string;
    trialText: string;
    subscription?: {
        status: string;
        cancel_at_period_end?: boolean;
    };
}

// Basic styling for Stripe elements (can be customized further)
const stripeElementOptions = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
                color: '#aab7c4',
            },
            padding: '10px 12px', // Mimic Chakra input padding
        },
        invalid: {
            color: '#9e2146',
        },
    },
};

const CheckoutForm = ({ priceId, trialText, subscription }: Props) => {
    const router = useNavigate();
    const { fetchUserData } = useUserData();
    const stripe = useStripe();
    const elements = useElements();
    const toast = useToast();
    const { showError } = useError();
    const [{ get, post }] = useServerApi();

    // Payment sources state
    const [paymentSources, setPaymentSources] = useState<any[]>([]);
    const [selectedSource, setSelectedSource] = useState('');
    const [hideCardElements, setHideCardElements] = useState(false);
    const [loading, setLoading] = useState(false);

    // Fetch payment sources
    const fetchPaymentSources = useCallback(async () => {
        setLoading(true);
        const [paymentSuccess, paymentResponse] = await get('/rest_endpoints/stripe/crud/payment_sources');
        if (paymentSuccess) {
            const paymentSources = paymentResponse.payment_sources ?? [];
            setPaymentSources(paymentSources);
            setSelectedSource(paymentSources.length && paymentSources[0] > 0 ? paymentSources[0].payment_id : '');
            setHideCardElements(paymentSources.length > 0);
        }
        setLoading(false);
    }, [get]);

    let trialDays = 7;
    if (subscription && subscription.status === 'canceled') {
        trialDays = 0;
    }
    if (subscription && subscription.status === 'trialing' && subscription.cancel_at_period_end) {
        trialDays = 0;
    }

    useQuery(GetMe, {
        onError(error) {
            showError(undefined, error);
        },
    });

    // Fetch payment sources on mount

    useEffect(() => {
        fetchPaymentSources();
    }, [fetchPaymentSources]);

    useMutation(CreateSubscription, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const [isStripePaymentProcessing, setIsStripePaymentProcessing] = useState(false);
    const [nameOnCard, setNameOnCard] = useState('');
    const [billingZip, setBillingZip] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [stripeElementError, setStripeElementError] = useState<string | null>(null);

    const handleStripeElementChange = (event: any) => {
        if (event.error) {
            setStripeElementError(event.error.message);
        } else {
            setStripeElementError(null);
        }
    };

    const createPaymentMethodHandler = async (cardNumberElement: StripeCardNumberElement) => {
        if (!stripe) {
            throw new Error("Stripe.js hasn't loaded yet.");
        }

        const { error, paymentMethod }: IPaymentMethodResult = await stripe.createPaymentMethod({
            type: 'card',
            card: cardNumberElement,
            billing_details: {
                name: nameOnCard,
                address: {
                    postal_code: billingZip,
                },
            },
        });

        if (error) {
            throw error; // Let the main handler catch and display
        }
        if (!paymentMethod) {
            throw new Error('Failed to create payment method.');
        }
        return paymentMethod;
    };

    // const createSubscriptionHandler = async (paymentMethodId: string) => {
    // Function body removed to fix parsing error

    // const confirmCardSetupHandler = async (clientSecret: string, cardNumberElement: StripeCardNumberElement) => {
    // Function body removed to fix parsing error

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsStripePaymentProcessing(true);
        setStripeElementError(null); // Clear previous errors

        if (!stripe || !elements) {
            toast({ status: 'error', title: 'Stripe is not initialized. Try reloading the page.' });
            setIsStripePaymentProcessing(false);
            return;
        }

        if (!agreedToTerms) {
            toast({ status: 'warning', title: 'Please agree to the terms and conditions.' });
            setIsStripePaymentProcessing(false);
            return;
        }
        const subscribeWithPaymentSource = async (priceId: string, trialDays = 7) => {
            try {
                const [subSuccess, subResponse] = await post('/rest_endpoints/stripe/crud/create_subscription', { priceId, trialDays });
                if (!subSuccess) {
                    throw new Error(subResponse?.detail || 'Failed to create subscription.');
                }
                await fetchUserData();
                if (trialDays === 0) {
                    router(PATHS.MY_AGENT);
                    return;
                }
                if (trialDays > 0 && typeof window !== 'undefined' && window.fbq) {
                    window.fbq('track', 'StartTrial');
                }
                router(PATHS.PHONE_PICK);
            } catch (error: any) {
                const errorMessage = error?.message || 'An unexpected error occurred.';
                toast({ status: 'error', title: errorMessage });
                setStripeElementError(errorMessage);
            }
        };

        // 4. Success Navigation

        if (selectedSource !== "") {
            await subscribeWithPaymentSource(priceId, trialDays);
            return;
        }

        const cardNumberElement = elements.getElement(CardNumberElement);
        const cardExpiryElement = elements.getElement(CardExpiryElement);
        const cardCvcElement = elements.getElement(CardCvcElement);

        if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
            toast({ status: 'error', title: 'Card details components are not ready.' });
            setIsStripePaymentProcessing(false);
            return;
        }

        try {
            // 1. Create Payment Method
            const paymentMethod = await createPaymentMethodHandler(cardNumberElement);

            // 2. Add Payment Source via useServerApi
            const [addSourceSuccess, addSourceResponse] = await post('/rest_endpoints/stripe/crud/add_payment_source', { payment_id: paymentMethod.id });
            if (!addSourceSuccess) {
                throw new Error(addSourceResponse?.detail || 'Failed to add payment source.');
            }
            await subscribeWithPaymentSource(priceId, trialDays);
            return;

        } catch (error: any) {
            // Handle Stripe errors and other errors
            const errorMessage = error?.message || 'An unexpected error occurred.';
            toast({ status: 'error', title: errorMessage });
            setStripeElementError(errorMessage); // Display error near elements if applicable
        } finally {
            setIsStripePaymentProcessing(false);
        }
    };
    if (loading) {
        return <Box>Loading...</Box>;
    }

    return (
        <form onSubmit={handleSubmit}>
            <VStack spacing='5' align='stretch'>
                {/* Payment Source Dropdown */}
                {hideCardElements && (<FormControl>
                    <FormLabel fontSize='sm'>Select a payment source</FormLabel>
                    <Select
                        value={selectedSource}
                        onChange={e => setSelectedSource(e.target.value)}
                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0', width: '100%' }}
                    >
                        <option value="">Select a payment source</option>
                        {paymentSources.slice(0, 3).map(source => (
                            <option key={source.payment_id} value={source.payment_id}>
                                **** **** **** {source.last4}
                            </option>
                        ))}
                    </Select>
                    <Link mt={2} color="blue.500" onClick={() => { setHideCardElements(false); setSelectedSource(""); }}>Add New Card</Link>
                </FormControl>)}

                {/* Name on Card */}
                {!hideCardElements && (<>
                    {paymentSources.length > 0 && <Link mt={2} color="blue.500" onClick={() => { setHideCardElements(true); fetchPaymentSources() }}>Show Saved Cards</Link>}
                    <FormControl isRequired>
                        <FormLabel fontSize='sm'>Name on card</FormLabel>
                        <Input placeholder='Full name' value={nameOnCard} onChange={(e) => setNameOnCard(e.target.value)} />
                    </FormControl>

                    {/* Card Number */}
                    <FormControl isRequired isInvalid={!!stripeElementError}>
                        <FormLabel fontSize='sm'>Card Number</FormLabel>
                        {/* Wrap Stripe element for styling consistency */}
                        <Box border='1px' borderColor='inherit' borderRadius='md' p='2.5'>
                            <CardNumberElement options={stripeElementOptions} onChange={handleStripeElementChange} />
                        </Box>
                        {/* Display Stripe element errors here if needed, or rely on toast */}
                        {/* <FormErrorMessage>{stripeElementError}</FormErrorMessage> */}
                    </FormControl>

                    {/* Expiry, CVC, Zip */}
                    <HStack spacing='4' align='flex-start'>
                        <FormControl isRequired flex='1'>
                            <FormLabel fontSize='sm'>Expiry Date</FormLabel>
                            <Box border='1px' borderColor='inherit' borderRadius='md' p='2.5'>
                                <CardExpiryElement options={stripeElementOptions} onChange={handleStripeElementChange} />
                            </Box>
                        </FormControl>
                        <FormControl isRequired flex='1'>
                            <FormLabel fontSize='sm'>CVC</FormLabel>
                            <Box border='1px' borderColor='inherit' borderRadius='md' p='2.5'>
                                <CardCvcElement options={stripeElementOptions} onChange={handleStripeElementChange} />
                            </Box>
                        </FormControl>
                        <FormControl isRequired flex='1'>
                            <FormLabel fontSize='sm'>Billing Zip</FormLabel>
                            <Input
                                placeholder='ZIP / Postal'
                                value={billingZip}
                                onChange={(e) => setBillingZip(e.target.value)}
                            />
                        </FormControl>
                    </HStack>
                </>)}

                {/* Display general Stripe element error */}
                {stripeElementError && (
                    <Box color='red.500' fontSize='sm' mt={-2}>
                        {stripeElementError}
                    </Box>
                )}
                {trialText}

                {/* Terms and Conditions */}
                <Checkbox isChecked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} isRequired>
                    I agree to Dialzara LLC's{' '}
                    <Link href='https://dialzara.com/terms-of-use/' isExternal color='blue.500'>
                        terms of use
                    </Link>{' '}
                    and{' '}
                    <Link href='https://dialzara.com/privacy-policy/' isExternal color='blue.500'>
                        privacy policy
                    </Link>
                </Checkbox>

                {/* Action Buttons */}
                <Flex justifyContent='space-between' mt='4'>
                    <Button onClick={() => router(PATHS.MY_AGENT)} variant='outline' fontSize='sm'>
                        Back
                    </Button>
                    <ZaraButton
                        variant='blackAlpha'
                        isLoading={isStripePaymentProcessing}
                        type='submit'
                        isDisabled={!stripe || !elements || isStripePaymentProcessing}
                    >
                        Next
                    </ZaraButton>
                </Flex>
            </VStack>
        </form >
    );
};

export default CheckoutForm;
